import { type ReactElement } from 'react';
import { buildTestingEntityMetricColgroup } from '@/features/shared/components/tableSystem/TableV2MetricColumnLayout';
import {
  tableV2ActionsColumnWidth,
  tableV2ChevronColumnWidth,
  type TableV2SkeletonColumnKind,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import type { MetricThresholdGroupKey } from '@/shared/utils/metricsThresholds';

const suiteBaseSkeletonColumns = [
  'chevron',
  'nameSingle',
  'status',
  'dateTime',
  'tags',
  'actions',
] satisfies TableV2SkeletonColumnKind[];

export const buildSuiteSkeletonColumns = (
  visibleMetricKeys: MetricThresholdGroupKey[],
  includeProject = false,
  includeActions = true,
): TableV2SkeletonColumnKind[] => [
  ...suiteBaseSkeletonColumns.slice(0, 4),
  ...visibleMetricKeys.map(() => 'metric' as const),
  ...suiteBaseSkeletonColumns.slice(4, 5),
  ...(includeProject ? (['project'] satisfies TableV2SkeletonColumnKind[]) : []),
  ...(includeActions ? suiteBaseSkeletonColumns.slice(5) : []),
];

export const suiteSkeletonColumns = buildSuiteSkeletonColumns([
  'passRate',
  'frequency',
  'avgDuration',
  'flakeScore',
]);

export const suiteRunSkeletonColumns = [
  'chevron',
  'name',
  'status',
  'passRate',
  'duration',
  'dateTime',
  'tags',
  'actions',
] satisfies TableV2SkeletonColumnKind[];

export const buildSuiteRunSkeletonColumns = (includeActions = true): TableV2SkeletonColumnKind[] =>
  includeActions ? suiteRunSkeletonColumns : suiteRunSkeletonColumns.slice(0, -1);

export const testRunSkeletonColumns = [
  'name',
  'status',
  'duration',
  'dateTime',
  'tags',
  'actions',
] satisfies TableV2SkeletonColumnKind[];

export const buildTestRunSkeletonColumns = (includeActions = true): TableV2SkeletonColumnKind[] =>
  includeActions ? testRunSkeletonColumns : testRunSkeletonColumns.slice(0, -1);

export const buildSuiteColgroup = (
  visibleMetricKeys: MetricThresholdGroupKey[],
  includeProject = false,
  includeActions = true,
): ReactElement =>
  buildTestingEntityMetricColgroup(visibleMetricKeys, includeProject, includeActions);

export const suiteColgroup = buildSuiteColgroup([
  'passRate',
  'frequency',
  'avgDuration',
  'flakeScore',
]);

export const buildSuiteRunColgroup = (includeActions = true): ReactElement => (
  <colgroup>
    <col width={tableV2ChevronColumnWidth} />
    <col width={includeActions ? '20%' : '22%'} />
    <col width={includeActions ? '9%' : '10%'} />
    <col width="14%" />
    <col width="11%" />
    <col width={includeActions ? '20%' : '21%'} />
    <col width={includeActions ? '18%' : '20%'} />
    {includeActions ? <col width={tableV2ActionsColumnWidth} /> : null}
  </colgroup>
);

export const suiteRunColgroup = buildSuiteRunColgroup();

export const buildTestRunColgroup = (includeActions = true): ReactElement => (
  <colgroup>
    <col width={includeActions ? '24%' : '26%'} />
    <col width="11%" />
    <col width="13%" />
    <col width={includeActions ? '24%' : '25%'} />
    <col width={includeActions ? '22%' : '25%'} />
    {includeActions ? <col width={tableV2ActionsColumnWidth} /> : null}
  </colgroup>
);

export const testRunColgroup = buildTestRunColgroup();
